import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deactivateThemeImport } from '@/api/theme-question-curation.api';
import { themeImportErrorMessage } from '@/api/theme-import-history.contract';
import {
  getThemeImportHistory,
  themeImportHistoryQueryKey,
  type ThemeImportStatusFilter,
} from '@/api/theme-import-history.api';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';

export type ThemeImportsStatusFilter = 'all' | ThemeImportStatusFilter;

export interface ThemeImportsFilterState {
  sector: string;
  status: ThemeImportsStatusFilter;
  search: string;
}

export interface ThemeImportsCounts {
  total: number;
  active: number;
  inactive: number;
}

function useDebouncedValue(value: string, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function useThemeImports() {
  const { t } = useApp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const copy = t.admin.excelFiles.themes;

  const [sector, setSector] = useState('all');
  const [status, setStatus] = useState<ThemeImportsStatusFilter>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const query = useInfiniteQuery({
    queryKey: ['theme-import-history', { sector, status, search: debouncedSearch }],
    queryFn: ({ pageParam }) =>
      getThemeImportHistory({
        cursor: pageParam,
        sector: sector === 'all' ? null : sector,
        status: status === 'all' ? null : status,
        search: debouncedSearch.length > 0 ? debouncedSearch : null,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.meta.pagination.next_cursor,
  });

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const counts = useMemo<ThemeImportsCounts>(() => {
    let activeCount = 0;
    let inactiveCount = 0;
    for (const row of rows) {
      if (row.status === 'active') activeCount += 1;
      else inactiveCount += 1;
    }
    return { total: rows.length, active: activeCount, inactive: inactiveCount };
  }, [rows]);

  const sectors = useMemo(
    () => Array.from(new Set(rows.map((row) => row.sectorName))).sort(),
    [rows],
  );

  const deleteMutation = useMutation({
    mutationFn: deactivateThemeImport,
    onSuccess: () => {
      toast({ title: t.success, description: copy.deleteSuccess });
      void queryClient.invalidateQueries({ queryKey: themeImportHistoryQueryKey });
    },
    onError: (error) => {
      toast({ title: t.error, description: themeImportErrorMessage(error, copy.errors), variant: 'destructive' });
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: themeImportHistoryQueryKey });
  };

  return {
    query,
    rows,
    counts,
    sectors,
    sector,
    setSector,
    status,
    setStatus,
    search,
    setSearch,
    deleteMutation,
    refresh,
  };
}
